import {
  DOCUMENT_CONTENT_TYPES,
  EMPLOYMENT_TYPES,
  LICENCE_BODIES,
  LICENCE_NOTICE_KINDS,
  MAX_DEPARTMENT_LENGTH,
  MAX_DOCUMENT_BYTES,
} from '@wodi/domain';
import { z } from 'zod';

import { actorSchema, isoDaySchema, RECORD_STATUSES } from './clinical-common.ts';
import { licenceStatusSchema, practitionerCadreSchema } from './practitioners.ts';

// Staff records with licence expiry (docs/plans/phase2-staff-records.md §3). The licence
// details come from the practitioner profile (L3); the employment details and licence
// copies are the staff record's own.

export const staffLicenceSchema = z.object({
  cadre: practitionerCadreSchema.nullable(),
  licenceBody: z.enum(LICENCE_BODIES).nullable(),
  /** Shown in mono. */
  licenceNumber: z.string().nullable(),
  licenceIssuedOn: isoDaySchema.nullable(),
  licenceExpiresOn: isoDaySchema.nullable(),
  /** `no_profile` when the person has no active practitioner profile. */
  licence: licenceStatusSchema,
});

export const staffEmploymentSchema = z.object({
  department: z.string().nullable(),
  employmentType: z.enum(EMPLOYMENT_TYPES),
  startDate: isoDaySchema.nullable(),
});
export type StaffEmploymentView = z.infer<typeof staffEmploymentSchema>;

export const staffListItemSchema = staffLicenceSchema.extend({
  userId: z.uuid(),
  displayName: z.string(),
  userStatus: z.enum(['active', 'disabled']),
  /** Null until someone records the employment details. */
  employment: staffEmploymentSchema.nullable(),
  hasLicenceCopy: z.boolean(),
});
export type StaffListItemView = z.infer<typeof staffListItemSchema>;

export const staffListSchema = z.object({
  /** "Expires soon" this many days ahead: the organisation's setting (plan S2). */
  licenceWarningDays: z.number().int(),
  items: z.array(staffListItemSchema),
});
export type StaffListView = z.infer<typeof staffListSchema>;

export const staffDocumentSchema = z.object({
  id: z.uuid(),
  kind: z.literal('licence'),
  contentType: z.enum(DOCUMENT_CONTENT_TYPES),
  sizeBytes: z.number().int(),
  uploadedAt: z.iso.datetime(),
  uploadedBy: actorSchema,
  status: z.enum(RECORD_STATUSES),
  enteredInError: z
    .object({ by: actorSchema, at: z.iso.datetime(), reason: z.string() })
    .nullable(),
});
export type StaffDocumentView = z.infer<typeof staffDocumentSchema>;

export const staffRecordSchema = staffListItemSchema.extend({
  licenceWarningDays: z.number().int(),
  /** Licence copies, newest first, including ones marked entered in error. */
  documents: z.array(staffDocumentSchema),
});
export type StaffRecordView = z.infer<typeof staffRecordSchema>;

export const saveStaffEmploymentRequestSchema = z.object({
  department: z
    .string()
    .max(MAX_DEPARTMENT_LENGTH + 20)
    .nullable(),
  employmentType: z.enum(EMPLOYMENT_TYPES),
  startDate: isoDaySchema.nullable(),
});
export type SaveStaffEmploymentRequest = z.input<typeof saveStaffEmploymentRequestSchema>;

const MAX_BASE64_LENGTH = Math.ceil(MAX_DOCUMENT_BYTES / 3) * 4;

export const uploadStaffDocumentRequestSchema = z.object({
  clientRequestId: z.uuid(),
  kind: z.literal('licence'),
  contentBase64: z
    .string()
    .max(MAX_BASE64_LENGTH + 4)
    .regex(/^[A-Za-z0-9+/]*={0,2}$/, 'Must be the file encoded as base64'),
});
export type UploadStaffDocumentRequest = z.input<typeof uploadStaffDocumentRequestSchema>;

/** A notice the daily job recorded (plan §2.4), for Home. */
export const licenceNoticeSchema = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  displayName: z.string(),
  kind: z.enum(LICENCE_NOTICE_KINDS),
  licenceBody: z.enum(LICENCE_BODIES).nullable(),
  licenceExpiresOn: isoDaySchema,
  detectedOn: isoDaySchema,
});
export type LicenceNoticeView = z.infer<typeof licenceNoticeSchema>;

export const licenceNoticeListSchema = z.object({
  items: z.array(licenceNoticeSchema),
  /** Live counts, so the notice agrees with the list even before the job runs. */
  counts: z.object({ expired: z.number().int(), expiring: z.number().int() }),
  licenceWarningDays: z.number().int(),
});
export type LicenceNoticeListView = z.infer<typeof licenceNoticeListSchema>;
